/**
 * Language Toggle — Onboarding page
 * Segmented EN / ES / DE control. Purely page-scoped: no Shopify Markets,
 * no URL change. Persists choice in localStorage and exposes a small API
 * plus a custom event so other scripts (the i18n content-swap) can react.
 */
(function () {
  const STORAGE_KEY = 'offbits_onboarding_lang';
  const SUPPORTED = ['en', 'es', 'de'];
  const DEFAULT_LANG = 'en';

  function getStoredLang() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return SUPPORTED.includes(stored) ? stored : DEFAULT_LANG;
    } catch (e) {
      return DEFAULT_LANG;
    }
  }

  function setStoredLang(lang) {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      /* localStorage unavailable — selection just won't persist across visits */
    }
  }

  function applyLang(lang, options) {
    options = options || {};
    document.documentElement.setAttribute('data-lang', lang);

    document.querySelectorAll('.lang-toggle').forEach((toggle) => {
      setActiveOption(toggle, lang, options.animate !== false);
    });

    if (!options.silent) {
      document.dispatchEvent(new CustomEvent('offbits:langchange', { detail: { lang: lang } }));
    }
  }

  function setActiveOption(toggle, lang, animate) {
    const options = Array.from(toggle.querySelectorAll('.lang-toggle__option'));
    const pill = toggle.querySelector('.lang-toggle__pill');
    const target = options.find((btn) => btn.dataset.lang === lang);
    if (!target) return;

    options.forEach((btn) => {
      btn.setAttribute('aria-selected', btn === target ? 'true' : 'false');
    });

    if (pill && options.length) {
      // Measure the real pixel distance from the first button rather than
      // assuming index * 100% — that assumption breaks as soon as there's
      // a gap between buttons, since 100% only covers the button's own
      // width and ignores the gap it also needs to travel.
      const offset = target.offsetLeft - options[0].offsetLeft;

      if (!animate) {
        pill.style.transition = 'none';
      }
      pill.style.transform = 'translateX(' + offset + 'px)';
      if (!animate) {
        // Force reflow so the transition-none takes effect before re-enabling it
        pill.getBoundingClientRect();
        pill.style.transition = '';
      }
    }
  }

  function sizeToggle(toggle) {
    const options = Array.from(toggle.querySelectorAll('.lang-toggle__option'));
    let widest = 0;
    options.forEach((btn) => {
      widest = Math.max(widest, btn.getBoundingClientRect().width);
    });
    // Buttons already include their own padding around the fixed-size flag,
    // so no extra breathing room is added here — keeps the pill a true circle.
    toggle.style.setProperty('--lang-toggle-option-width', Math.ceil(widest) + 'px');
  }

  function initToggle(toggle) {
    sizeToggle(toggle);

    toggle.querySelectorAll('.lang-toggle__option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang;
        setStoredLang(lang);
        applyLang(lang);
      });
    });
  }

  function init() {
    const toggles = document.querySelectorAll('.lang-toggle');
    if (!toggles.length) return;

    toggles.forEach(initToggle);

    const initialLang = getStoredLang();
    applyLang(initialLang, { animate: false, silent: true });

    // Re-sync pill position on resize (label widths / wrapping can shift)
    window.addEventListener('resize', () => {
      toggles.forEach((toggle) => {
        sizeToggle(toggle);
        setActiveOption(toggle, getStoredLang(), false);
      });
    });
  }

  // Small public API for the upcoming i18n content-swap script
  window.OffbitsLang = {
    get: getStoredLang,
    set: function (lang) {
      if (!SUPPORTED.includes(lang)) return;
      setStoredLang(lang);
      applyLang(lang);
    },
    SUPPORTED: SUPPORTED
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();